import type { ReactNode } from "react";
import { Platform, StyleSheet, Text, View, type StyleProp, type TextStyle } from "react-native";
import { useTranslation } from "react-i18next";
import { PROGRAM_FOCUS, type ProgramFocus } from "@heraion/shared";

import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import type { UiTheme } from "../theme";

type Translate = (key: string, opts?: { defaultValue?: string }) => string;

/** A focus in words: the chips here, and a catalog card's goal (#472). */
export const focusLabel = (f: ProgramFocus, t: Translate): string =>
  f === "mass"
    ? t("editorV3.model.focusMass", { defaultValue: "Mass" })
    : t("editorV3.model.focusStrength", { defaultValue: "Strength" });

/** The stand-in is never reached: no screen reader, no tap, and on the web
 *  no Tab either (`inert`), since its chips are still buttons. */
const hidden = {
  pointerEvents: "none",
  "aria-hidden": true,
  accessibilityElementsHidden: true,
  importantForAccessibility: "no-hide-descendants",
  ...(Platform.OS === "web" ? { inert: true } : {}),
} as const;

// #356: the strength/mass choice of a program model, with the hint that says
// what the choice does. The wizard and «Add W5-W8» show the same row. The
// hint changes with the choice, so it is a polite live region: a screen
// reader hears it. `reserve` keeps the room while the row is yet to come: the
// same row, unseen and unheard, so its height follows the font and the
// language, and nothing is announced.
export function ModelFocusRow({
  theme,
  focus,
  onSelect,
  hintStyle,
  reserve = false,
}: {
  theme: UiTheme;
  focus: ProgramFocus;
  onSelect: (focus: ProgramFocus) => void;
  hintStyle: StyleProp<TextStyle>;
  reserve?: boolean;
}) {
  const { t } = useTranslation();
  const label = (f: ProgramFocus) => focusLabel(f, t);
  const hint =
    focus === "mass"
      ? t("editorV3.model.focusMassHint", {
          defaultValue: "More sets and reps, shorter rests. The deload day becomes a volume day.",
        })
      : t("editorV3.model.focusStrengthHint", {
          defaultValue: "Few reps on the main lifts, long rests. The deload day stays light.",
        });
  // Each part veiled on its own: the two stay siblings in the caller's
  // column, so its gap sits between them in both states.
  const veil = (node: ReactNode) =>
    reserve ? (
      <View style={styles.unseen} {...hidden}>
        {node}
      </View>
    ) : (
      node
    );
  return (
    <>
      {veil(
        <ChipChoiceRow
          theme={theme}
          choices={PROGRAM_FOCUS}
          selected={focus}
          onSelect={onSelect}
          label={label}
          chipA11yLabel={label}
          groupA11yLabel={t("editorV3.model.focus", { defaultValue: "Focus" })}
          title={t("editorV3.model.focus", { defaultValue: "Focus" })}
        />,
      )}
      {veil(
        <Text style={hintStyle} {...(reserve ? {} : ({ accessibilityLiveRegion: "polite", "aria-live": "polite" } as const))}>
          {hint}
        </Text>,
      )}
    </>
  );
}

const styles = StyleSheet.create({ unseen: { opacity: 0 } });
